/**
 * Interweave Transform for Interactive HTML Elements
 *
 * This transform processes HTML elements with data-interactive attributes
 * and replaces them with React components.
 */

import type { TransformCallback } from 'interweave';
import React from 'react';

import { ExpandableSection } from './components/ExpandableSection';
import { FaqAccordion } from './components/FaqAccordion';
import { InteractiveSlider } from './components/InteractiveSlider';
import { ModalTrigger } from './components/ModalTrigger';
import { TabsContainer } from './components/TabsContainer';
import type { InteractiveType } from './types';
import { parseModalConfig, parseFaqConfig, parseSliderConfig, parseTabsConfig, parseExpandableConfig } from './utils';

import { logger } from '@logger';

/**
 * Helper to serialize node children to HTML string
 */
const serializeChildren = (node: any): string => {
  // Try innerHTML first (if available)
  if (node.innerHTML) {
    return node.innerHTML;
  }

  // Fallback: manually serialize child nodes
  const nodeToHtml = (child: any): string => {
    if (!child) return '';

    // Text node
    if (child.nodeType === 3) {
      return child.textContent || '';
    }

    // Element node
    if (child.nodeType === 1) {
      const tagName = child.tagName.toLowerCase();
      let html = `<${tagName}`;

      // Add attributes
      if (child.attributes) {
        for (let i = 0; i < child.attributes.length; i++) {
          const attr = child.attributes[i];
          html += ` ${attr.name}="${attr.value}"`;
        }
      }

      html += '>';

      // Add children
      const children = child.childNodes || child.children || [];
      for (const grandchild of children) {
        html += nodeToHtml(grandchild);
      }

      html += `</${tagName}>`;
      return html;
    }

    return '';
  };

  const children = node.childNodes || node.children || [];
  let html = '';
  for (const child of children) {
    html += nodeToHtml(child);
  }

  return html || node.textContent || '';
};

/**
 * Transform callback for Interweave
 *
 * @param node - The HTML element being processed
 * @param children - Parsed children nodes
 * @returns React component or undefined
 */
export const interactiveHtmlTransform: TransformCallback = (node, _children) => {
  // Only process elements with data-interactive attribute
  const interactiveType = node.getAttribute('data-interactive') as InteractiveType | null;

  if (!interactiveType) {
    return undefined; // Let Interweave handle normally
  }

  // Preserve original classes
  const className = node.getAttribute('class') || '';

  // Process based on interactive type
  switch (interactiveType) {
    case 'modal': {
      const config = parseModalConfig(node);
      // Preserve inner HTML to keep formatting, spans, etc.
      const buttonHtml = serializeChildren(node) || 'Open Modal';
      return <ModalTrigger config={config} buttonHtml={buttonHtml} className={className} />;
    }

    case 'faq': {
      const config = parseFaqConfig(node);
      return <FaqAccordion config={config} className={className} />;
    }

    case 'slider': {
      const config = parseSliderConfig(node);
      return <InteractiveSlider config={config} className={className} />;
    }

    case 'tabs': {
      const config = parseTabsConfig(node);
      return <TabsContainer config={config} className={className} />;
    }

    case 'expandable': {
      const config = parseExpandableConfig(node);
      return <ExpandableSection config={config} className={className} />;
    }

    default:
      // Unknown type, warn and return undefined
      logger.warn(`Unknown interactive type: ${interactiveType}`);
      return undefined;
  }
};
